//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { fetchDefaultInstagramAccount } from '@instagram-account/redux/account/fetchDefaultInstagramAccount';
import {
  SCHEDULE_ITEM_TASK_NAME,
  hasPermissionToSchedule,
} from '@instagram-account/redux/account/instagramAccountModel';
import {
  setSessionStateInfo,
  updateInsightsMetadata,
} from '@instagram-account/redux/account/instagramAccountSlice';
import { instagramAccountAudienceActions } from '@instagram-account/redux/audience/instagramAccountAudienceActions';
import { fetchOnlineFollowersHeatMap } from '@instagram-account/redux/audiencev2/instagramAudienceSlice';
import { instagramAccountEngagementPostsActions } from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsActions';
import {
  getInstagramUserStatsActions,
  getMetricsGatewayApiService,
  getPromotedPostsMetricsActions,
} from '@react-bridge/wrappers/ng-service-provider';
import { createThunk } from '@redux/createThunk';

export const instagramAccountActions = {
  // TODO DEV-51162: Remove this copy of session state info
  loadSessionState: createThunk(() => (dispatch, getState) => {
    dispatch(
      setSessionStateInfo({
        timeZoneId: getState().session.timeZone.id,
        timeZoneShortName: getState().session.timeZone.shortName,
        hasSchedulingPermissions: hasPermissionToSchedule(),
        schedulingTaskName: SCHEDULE_ITEM_TASK_NAME,
      }),
    );
  }),

  getInsightsMetadata: createThunk(() => async (dispatch) => {
    const metadata = await getMetricsGatewayApiService().getMetadata();
    dispatch(updateInsightsMetadata(metadata));
  }),

  fetchDefaultInstagramAccount,

  initializeIgData: createThunk(() => async (dispatch, getState) => {
    dispatch(instagramAccountActions.loadSessionState());

    await Promise.all([
      dispatch(instagramAccountActions.fetchDefaultInstagramAccount()),
      dispatch(instagramAccountActions.getInsightsMetadata()),
      dispatch(
        getInstagramUserStatsActions().fetchHMSAudienceInsightsStartDate(),
      ),
    ]);

    const defaultAccount = getState().instagram.account.defaultInstagramAccount;

    const { accountId, username } = defaultAccount ?? {};

    /**
     * We should never make it into the IG dashboard without these fields defined
     * on the default user, but bail out if we do.
     */
    if (!username || !accountId) {
      return;
    }

    const { summaryDefaultDateRange } = getState().instagramUserStats;

    // Preload any information required to display the complete UI of the IG summary page
    await Promise.all([
      dispatch(getInstagramUserStatsActions().fetchSummaryUserStats(accountId)),
      dispatch(getInstagramUserStatsActions().fetchSummaryTrafficStats()),
      dispatch(
        instagramAccountAudienceActions.retrieveSummaryTopRecentEngagers(
          summaryDefaultDateRange,
        ),
      ),
      dispatch(
        instagramAccountEngagementPostsActions.fetchMentionedAndTaggedPosts(
          username,
        ),
      ),
    ]);

    // Preload all posts for the engagement posts, mentions and tags, and the schedule feed preview
    void dispatch(
      instagramAccountEngagementPostsActions.fetchRecentBrandInstagramPosts(),
    );
    void dispatch(
      instagramAccountEngagementPostsActions.fetchLiveBrandInstagramPosts(),
    );

    // Preload promoted posts data
    void dispatch(getPromotedPostsMetricsActions().fetchRecentPromotedPosts());
    void dispatch(getPromotedPostsMetricsActions().fetchGAState());

    // Preload online follower data for the Instagram audience page and
    // the "Best Time to Post" functionality in the calendar and post details
    void dispatch(fetchOnlineFollowersHeatMap());
  }),
};
